import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Check, ExternalLink } from "lucide-react";
import api, { humanError } from "@/lib/api";
import { useAuth, can } from "@/lib/auth";

/**
 * Plan and billing.
 *
 * Subscribing hands off to the payment provider and comes back through a
 * webhook, so this deliberately does not congratulate anyone on upgrading. It
 * says the payment is being set up and leaves the plan alone until the server
 * confirms it -- reaching a checkout page is not the same as paying, and a UI
 * that assumes otherwise shows a plan the customer has not bought.
 */
export default function BillingPanel() {
  const { role } = useAuth();
  const canAdmin = can(role, "tenant.settings");

  const [state, setState] = useState(null);
  const [busy, setBusy] = useState("");

  const load = () =>
    api.get("/billing/status")
      .then(({ data }) => setState(data))
      .catch(() => setState(null));

  useEffect(() => { load(); }, []);

  async function subscribe(planCode) {
    setBusy(planCode);
    try {
      const { data } = await api.post("/billing/subscribe", { plan_code: planCode });
      if (data.short_url) {
        // The provider owns the payment screen.
        window.location.href = data.short_url;
        return;
      }
      toast.success("Payment set up. Your plan changes once the payment clears.");
      load();
    } catch (e) {
      toast.error(humanError(e));
    } finally {
      setBusy("");
    }
  }

  async function cancel() {
    if (!window.confirm("Cancel your subscription? Your plan stays active until the end of the current cycle.")) return;
    setBusy("cancel");
    try {
      const { data } = await api.post("/billing/cancel");
      toast.success(data.message);
      load();
    } catch (e) {
      toast.error(humanError(e));
    } finally {
      setBusy("");
    }
  }

  if (!state) return null;

  const current = state.plan_code;
  const trial = state.trial || {};
  const money = (p) =>
    p.is_custom_priced ? "Custom" : `$${Math.round(p.price_monthly / 100)}/mo`;
  const limit = (n) => (n === -1 ? "Unlimited" : n);

  return (
    <section className="rounded-xl border border-[#E4E9EF] bg-white p-5" data-testid="billing-panel">
      <div className="mb-1 flex flex-wrap items-center justify-between gap-2">
        <div className="text-sm font-semibold text-[#0B2B33]">Plan and billing</div>
        {trial.in_trial ? (
          <span className="hs-chip bg-[#E8FBF1] text-[#16A46B]" data-testid="trial-chip">
            Trial — {trial.days_left} day{trial.days_left === 1 ? "" : "s"} left
          </span>
        ) : state.subscription_active ? (
          <span className="hs-chip bg-[#E8FBF1] text-[#16A46B]">Active subscription</span>
        ) : null}
      </div>

      {!state.billing_enabled ? (
        <p className="mb-4 rounded-lg bg-[#FFF4E2] px-4 py-3 text-sm text-[#43535E]">
          Payments are not configured on this deployment yet, so plans cannot be changed here.
          Contact support to move plan.
        </p>
      ) : null}

      <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-4">
        {(state.plans || []).map((p) => {
          const isCurrent = p.code === current;
          return (
            <div
              key={p.code}
              data-testid={`billing-plan-${p.code}`}
              className={`rounded-lg border p-4 ${isCurrent ? "border-[#1F5FD8] ring-1 ring-[#1F5FD8]" : "border-[#E4E9EF]"}`}
            >
              <div className="flex items-baseline justify-between">
                <div className="font-semibold text-[#0B2B33]">{p.name}</div>
                {isCurrent ? <Check className="h-4 w-4 text-[#1F5FD8]" /> : null}
              </div>
              <div className="mt-1 font-display text-2xl font-bold tabular-nums text-[#0B2B33]">
                {money(p)}
              </div>
              <ul className="mt-3 space-y-1 text-xs text-[#43535E]">
                <li>{limit(p.max_seats)} seat{p.max_seats === 1 ? "" : "s"}</li>
                <li>{limit(p.max_social_accounts)} social accounts</li>
              </ul>

              {isCurrent ? (
                <div className="mt-4 text-xs font-semibold text-[#1F5FD8]">Your plan</div>
              ) : canAdmin && state.billing_enabled ? (
                <button
                  onClick={() => subscribe(p.code)}
                  disabled={!!busy}
                  data-testid={`billing-choose-${p.code}`}
                  className="mt-4 w-full rounded-full border border-[#E4E9EF] px-3 py-1.5 text-sm font-semibold text-[#0B2B33] hover:bg-[#F6F8FA] disabled:opacity-60"
                >
                  {busy === p.code ? "Opening…" : p.is_custom_priced ? "Contact sales" : "Choose"}
                </button>
              ) : null}
            </div>
          );
        })}
      </div>

      {state.subscription ? (
        <div className="mt-4 flex flex-wrap items-center justify-between gap-3 border-t border-[#E4E9EF] pt-4">
          <div className="text-sm text-[#43535E]">
            Subscription <span className="font-semibold text-[#0B2B33]">{state.subscription.status}</span>
            {state.subscription.plan_code ? ` on ${state.subscription.plan_code}` : ""}
          </div>
          {canAdmin && state.subscription.status === "active" ? (
            <button onClick={cancel} disabled={!!busy} data-testid="billing-cancel"
              className="rounded-full border border-[#E4E9EF] px-3 py-1.5 text-sm font-semibold text-[#B02328] hover:bg-[#FDECEC] disabled:opacity-60">
              {busy === "cancel" ? "Cancelling…" : "Cancel subscription"}
            </button>
          ) : null}
        </div>
      ) : null}

      {trial.in_trial ? (
        <p className="mt-4 text-xs text-[#8A97A0]">
          Your trial ends on {new Date(trial.ends_at).toLocaleDateString()}. Without a subscription
          the workspace moves to Standard — nothing is deleted.
        </p>
      ) : null}

      <a href="/#pricing" className="mt-4 inline-flex items-center gap-1 text-xs font-semibold text-[#1F5FD8] hover:underline">
        Compare plans <ExternalLink className="h-3 w-3" />
      </a>
    </section>
  );
}
